import * as React from "react";
import {FC, useState} from "react";
import {
   EnumBrandSettingStockItemConfigColorStrategy,
   EnumBrandSettingStockItemConfigImageSorting,
   EnumBrandSettingStockItemConfigMasterImageTypes,
   EnumBrandSettingStockItemConfigSkuStrategy,
   SettingsStockItemFragment,
   useBasicSettingsShopQuery,
   useBasicSettingsStockQuery,
   useBasicSettingsStockUpdateMutation
} from "../../../Queries";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {BodyText} from "../../../layout/Typography";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import {FormInputSelect} from "../../shop/wholesale/form/FormInputSelect";
import {FormInputCheckbox} from "../../shop/wholesale/form/FormInputCheckbox";
import {FormInputHtml} from "../../shop/wholesale/form/FormInputHtml";
import {SavingUI} from "../../../SavingProvider";
import {Dirty, DirtyDetector, ReactHookDirtyDetector} from "../../../components/DirtyDetector";
import {LocalizationEditor} from "../../shop/components/LocalizationEditor";
import {Wildcards} from "./Wildcards";
import SettingsHeader from "../SettingsHeader";
import {ShopTabs} from "../ShopTabs";
import PageContent from "../../../layout/PageContent";
import {FormInputNumber} from "../../shop/wholesale/form/FormInputNumber";
import {FormInputSelectMultiple} from "../../shop/wholesale/form/FormInputSelectMultiple";

const ID_STOCKFORM = "stocksettingsform";

const BasicItemSettingsPath = (brandId: string, shopId: string) => {
   return "/brands/" + brandId + "/settings/basic/" + shopId
}

export const BasicItemSettings = () => {
   const {brandHouseId, shopId} = useParams<{ brandHouseId: string, shopId: string }>();
   const navigate = useNavigate();

   const handleChange = (newValue: string) => {
      navigate(BasicItemSettingsPath(brandHouseId, newValue));
   };

   return <div>
      <SettingsHeader title={"Title and texts"}
                      subtitle={"Use this section to customize general settings for stockitems"}>
         <Button variant={"contained"} type={"submit"} form={ID_STOCKFORM}>
            Save
         </Button>
      </SettingsHeader>
      <ShopTabs
         activeId={shopId}
         brandHouseId={brandHouseId}
         onChange={handleChange}
         renderDefault={() => <BasicItemSettingsInnerCore brandHouseId={brandHouseId}/>}
         renderShop={(shop) => <BasicItemSettingsInner shopId={shop}/>}
      />
   </div>
}

type BasicItemSettingsInnerProps = {
   shopId?: string
   brandHouseId?: string
}

const BasicItemSettingsInner = ({shopId}: BasicItemSettingsInnerProps) => {
   const {data: staticData} = useBasicSettingsShopQuery({
      variables: {
         shopId: shopId
      }
   })

   const {data: settings} = useBasicSettingsStockQuery({
      variables: {
         shopId: shopId
      },
      fetchPolicy: "no-cache"
   })

   const [saveSettings] = useBasicSettingsStockUpdateMutation();
   const onSave = async (data: StockSettingsFormData) => {
      const {defaultCountryOfOrigin, ...stockConfig} = data;
      await SavingUI.process(async () => {
         await saveSettings({
            variables: {
               shopId: shopId,
               stockConfig: stockConfig,
               defaultCountryOfOrigin: normalizeCountryOfOrigin(defaultCountryOfOrigin)
            }
         });
         Dirty.reset()
      }, 'Stock settings updated')
   }

   if (!staticData || !settings) {
      return <LinearProgress/>
   }

   return <>
      <StockSettingsEditor
         scope={StockSettingsScope.Shop}
         primaryLocale={staticData.ShopById!.stockItemLocalePrimary}
         locales={staticData.ShopById!.stockItemLocales as string[]}
         data={{
            ...settings.BrandSettings!.stockItemConfig!,
            defaultCountryOfOrigin: settings.BrandSettings!.defaultCountryOfOrigin
         }}
         onSave={onSave}
      />
      <DirtyDetector/>
   </>
}

const BasicItemSettingsInnerCore = ({brandHouseId}: BasicItemSettingsInnerProps) => {
   const {data: settings} = useBasicSettingsStockQuery({
      variables: {
         brandHouseId: brandHouseId
      },
      fetchPolicy: "no-cache"
   })

   const [saveSettings] = useBasicSettingsStockUpdateMutation();
   const onSave = async (data: StockSettingsFormData) => {
      const {defaultCountryOfOrigin, ...stockConfig} = data;
      await SavingUI.process(async () => {
         await saveSettings({
            variables: {
               brandHouseId: brandHouseId,
               stockConfig: stockConfig,
               defaultCountryOfOrigin: normalizeCountryOfOrigin(defaultCountryOfOrigin)
            }
         });
         Dirty.reset()
      }, 'Stock settings updated')
   }

   if (!settings) {
      return <LinearProgress/>
   }

   // Extract locales where activeItems is true
   const activeLocales = settings.BrandSettings!.locales?.filter(l => l!.activeItems)?.map(l => l!.locale) || [];

   return <>
      <StockSettingsEditor
         scope={StockSettingsScope.Brand}
         locales={activeLocales}
         data={{
            ...settings.BrandSettings!.stockItemConfig!,
            defaultCountryOfOrigin: settings.BrandSettings!.defaultCountryOfOrigin
         }}
         onSave={onSave}
      />
      <DirtyDetector/>
   </>
}


type StockSettingsFormData = SettingsStockItemFragment & {
   defaultCountryOfOrigin?: string | null
}

const normalizeCountryOfOrigin = (value?: string | null) => {
   const country = (value || '').trim().toUpperCase();
   return country.length > 0 ? country : null;
}

/** Which settings the editor is editing: the brand house defaults or one shop's overrides. */
export enum StockSettingsScope {
   Brand = 'brand',
   Shop = 'shop'
}

type StockSettingsEditorProps = {
   /** Brand-wide settings such as the duplicate queue are only offered in the brand scope. */
   scope: StockSettingsScope
   primaryLocale?: string
   locales?: string[]
   data: StockSettingsFormData
   onSave: (data: StockSettingsFormData) => Promise<void>
}

const StockConfigurationSchema = yup.object().shape({
   titlePrefix: yup.string().required('Please enter a prefix'),
   titleSeperator: yup.string().nullable(),
   appendColor: yup.boolean().nullable(),
   appendSize: yup.boolean().nullable(),
   descriptionSuffix: yup.string().nullable(),
   imageAltText: yup.string().nullable(),
   requireReview: yup.boolean().nullable(),
   queueDuplicates: yup.boolean().nullable(),
   defaultCountryOfOrigin: yup.string().nullable()
      .matches(/^[A-Za-z]{2}$/, {
         excludeEmptyString: true,
         message: 'Please enter a 2 character ISO country code, e.g. DK'
      })
})

/** The stock item settings form shared by the brand tab and the shop tabs. */
export const StockSettingsEditor: FC<StockSettingsEditorProps> = ({
                                                              scope,
                                                              primaryLocale,
                                                              locales,
                                                              data,
                                                              onSave
                                                           }) => {
   const [showTranslations, setShowTranslations] = useState<boolean>(false);

   const methods = useForm<StockSettingsFormData>({
      resolver: yupResolver(StockConfigurationSchema),
      defaultValues: data,
      reValidateMode: "onChange"
   });
   const {control} = methods;

   const {fields, append, remove} = useFieldArray({
      control,
      name: 'itemRules'
   });

   const {fields: replacementFields, append: appendReplacement, remove: removeReplacement} = useFieldArray({
      control,
      name: 'replacements'
   });

   const {handleSubmit} = methods;

   const Localizations = () => {
      return <button onClick={() => setShowTranslations(true)}>Edit translations</button>
   }

   let showShopSettings = primaryLocale && locales;

   return <div>
      <form onSubmit={handleSubmit(onSave)} id={ID_STOCKFORM}/>
      <FormProvider {...methods}>
         <ReactHookDirtyDetector/>
         {showShopSettings &&
            <Dialog open={showTranslations} fullWidth maxWidth={"lg"} onClose={() => setShowTranslations(false)}>
               <DialogTitle>Localization editor</DialogTitle>
               <DialogContent>
                  <LocalizationEditor primaryLocale={primaryLocale!} locales={locales!} fields={[{
                     title: 'Stock item description',
                     field: 'descriptionSuffix',
                     multiline: true
                  }]}/>
               </DialogContent>
               <DialogActions>
                  <Button variant={"contained"} onClick={() => setShowTranslations(false)}>Update</Button>
               </DialogActions>
            </Dialog>
         }

         <PageContent>
            <BodyText type={"subtitle1"}>
               Stockitem texts configurations
            </BodyText>
            <Grid container gap={1}>
               <Grid item xs={12}>
                  <FormInputText name={"titlePrefix"} label={"Prefix"}/>
               </Grid>
               <Grid item xs={12}>
                  <FormInputText name={"titleAdvanced"} label={"Title format"}/>
               </Grid>
            </Grid>
            <Grid container gap={1} wrap={"nowrap"}>
               <Grid item xs={3}>
                  <FormInputCheckbox name={"appendColor"}
                                     label={"Append color to title (title - color)"}/>
               </Grid>
               <Grid item xs={3}>
                  <FormInputCheckbox name={"appendSize"} label={"Append size to title (title - size)"}/>
               </Grid>
               <Grid item xs={3}>
                  <FormInputSelect name={"titleSeperator"} label={"Prefix seperator"}
                                   options={[{
                                      id: '-',
                                      name: 'Dash (-)'
                                   }, {
                                      id: '/',
                                      name: 'Slash (/)'
                                   }]}
                  />
               </Grid>
               <Grid item xs={3}>
                  <FormInputSelect name={"appendSeperator"} label={"Suffix seperator"}
                                   options={[{
                                      id: '-',
                                      name: 'Dash (-)'
                                   }, {
                                      id: '/',
                                      name: 'Slash (/)'
                                   }]}/>
               </Grid>
            </Grid>
            <br/>

            {showShopSettings && <><br/><BodyText type={"subtitle1"}>
               Stock item description
            </BodyText>
               <Grid container gap={1} wrap={"nowrap"}>
                  <Grid item xs={12}>
                     <FormInputHtml name={"descriptionSuffix"}
                                    label={"Appendage to the stockitem description"}
                                    toolbarButton={[<Wildcards/>, <Localizations/>]}
                     />
                  </Grid>
               </Grid><br/></>}
            <BodyText type={"subtitle1"}>
               Storefront settings
            </BodyText>
            <Grid container gap={1} wrap={"nowrap"}>
               <Grid item xs={12}>
                  <FormInputText name={"template"} label={"Stock item template"}/>
               </Grid>
               <Grid item xs={12}>
                  <FormInputText name={"vendor"} label={"Stock item default vendor"}/>
               </Grid>
            </Grid>

            <br/>
            <BodyText type={"subtitle1"}>
               Images
            </BodyText>
            <Grid container gap={1} wrap={"nowrap"}>
               <Grid item xs={4}>
                  <FormInputSelect name={"imageSorting"}
                                   options={Object.values(EnumBrandSettingStockItemConfigImageSorting).map(sort => {
                                      return {
                                         id: sort,
                                         name: sort
                                      }
                                   })} label={"Sort behaviour"}/>
               </Grid>
               <Grid item xs={4}>
                  <FormInputText name={"imageAltText"} label={"Image alt texts (Default)"}/>
               </Grid>
               <Grid item xs={4}>
                  <FormInputText name={"originalImageAltText"} label={"Original image alt texts (Default)"}/>
               </Grid>
               <Grid item xs={4}>
                  <FormInputNumber name={"imageRotate"} label={"Automatic rotation of image (0-359)"}
                                   placeholder={"270"}/>
               </Grid>
            </Grid>

            <br/>
            <BodyText type={"subtitle1"}>
               Overrides
            </BodyText>
            <Grid container gap={1} wrap={"nowrap"}>
               <Grid item xs={4}>
                  <FormInputText name={"overrides.productType"} label={"Product type (override)"}/>
               </Grid>
               <Grid item xs={4}>
                  <FormInputText name={"overrides.vendor"} label={"Vendor (override)"}/>
               </Grid>
            </Grid>

            <br/>
            <BodyText type={"subtitle1"}>
               Country of origin
            </BodyText>
            <Grid container gap={1} wrap={"nowrap"}>
               <Grid item xs={4}>
                  <FormInputText name={"defaultCountryOfOrigin"}
                                 label={"Default country of origin (2 character ISO code)"}
                                 placeholder={"e.g. DK"}/>
               </Grid>
            </Grid>

            <br/>
            <BodyText type={"subtitle1"}>
               SKU &amp; Color Strategy
            </BodyText>
            <Grid container gap={1} wrap={"nowrap"}>
               <Grid item xs={4}>
                  <FormInputSelect name={"skuStrategy"} label={"The SKU generation strategy"}
                                   options={Object.keys(EnumBrandSettingStockItemConfigSkuStrategy).map(sku => ({
                                      id: sku,
                                      name: sku
                                   }))}/>
               </Grid>
               <Grid item xs={4}>
                  <FormInputSelect name={"colorStrategy"} label={"How to detect color from the product title"}
                                   options={Object.keys(EnumBrandSettingStockItemConfigColorStrategy).map(strategy => ({
                                      id: strategy,
                                      name: strategy
                                   }))}/>
               </Grid>
            </Grid>

            <br/>
            <BodyText type={"subtitle1"}>
               Auto images
            </BodyText>
            <Grid container gap={1} wrap={"nowrap"}>
               <Grid item xs={4}>
                  <FormInputNumber name={"masterImageCount"} label={"Number of images to add from master"}/>
               </Grid>
               <Grid item xs={4}>
                  <FormInputSelectMultiple name={"masterImageTypes"} label={"Image types to add from master"}
                                           options={Object.keys(EnumBrandSettingStockItemConfigMasterImageTypes).map(key => ({
                                              id: key,
                                              label: key
                                           }))}/>
               </Grid>
            </Grid>
            <br/>
            <BodyText type={"subtitle1"}>
               Publishing for sale
            </BodyText>
            <Grid container gap={1} wrap={"nowrap"}>
               <Grid item xs={12}>
                  <FormInputCheckbox name={"requireReview"} label={"Require review before items can be published"}/>
               </Grid>
            </Grid>
            {scope === StockSettingsScope.Brand && <>
               {/* Core reads the duplicate queue setting from the brand house only, so a shop tab has no say */}
               <br/>
               <BodyText type={"subtitle1"}>
                  Identical items
               </BodyText>
               <Grid container gap={1} wrap={"nowrap"}>
                  <Grid item xs={12}>
                     <FormInputCheckbox name={"queueDuplicates"}
                                        label={"Show one identical secondhand item (same style, colour and size) at a time; the others wait as queued until it sells"}/>
                  </Grid>
               </Grid>
            </>}
            <br/>
            <BodyText type={"subtitle1"}>
               Item Rules
            </BodyText>
            <Grid container gap={1} wrap={"nowrap"}>
               <Grid item xs={3}>
                  <FormInputSelect name={"itemRuleImages"} label={"How many packshots should we take"} options={[0,1,2,3,4,5].map(i => ({id: i, name: i + " images"}))}/>
               </Grid>
               <Grid item xs={9}>
                  <Grid container gap={1} wrap={"nowrap"}>
                     <Grid item xs={3}>
                        <FormInputCheckbox name={"itemRuleColor"} label={"Enable Color Rules"}/>
                     </Grid>
                     <Grid item xs={3}>
                        <FormInputCheckbox name={"itemRuleSize"} label={"Enable Size Rules"}/>
                     </Grid>
                     <Grid item xs={3}>
                        <FormInputCheckbox name={"itemRuleOther"} label={"Enable Other Rules"}/>
                     </Grid>
                     <Grid item xs={3}>
                        <FormInputCheckbox name={"itemRulePrice"} label={"Enable Price Rules"}/>
                     </Grid>
                  </Grid>
               </Grid>
            </Grid>
            <Grid container gap={1}>
               {fields.map((field, index) => (
                  <Grid item xs={12} key={field.id}>
                     <Grid container gap={1} alignItems="center">
                        <Grid item xs={5}>
                           <FormInputText
                              name={`itemRules.${index}.message`}
                              label={`Rule ${index + 1} Message`}
                           />
                        </Grid>
                        <Grid item xs={5}>
                           <FormInputText
                              name={`itemRules.${index}.expression`}
                              label={`Rule ${index + 1} Expression`}
                           />
                        </Grid>
                        <Grid item xs={1}>
                           <Button
                              variant="outlined"
                              color="error"
                              onClick={() => remove(index)}
                              size="small"
                           >
                              Remove
                           </Button>
                        </Grid>
                     </Grid>
                  </Grid>
               ))}
               <Grid item xs={12}>
                  <Button
                     variant="outlined"
                     onClick={() => append({message: "", expression: ""})}
                  >
                     Add custom rule
                  </Button>
               </Grid>
            </Grid>

            <br/>
            <BodyText type={"subtitle1"}>
               String Replacements
            </BodyText>
            <Grid container gap={1}>
               {replacementFields.map((field, index) => (
                  <Grid item xs={12} key={field.id}>
                     <Grid container gap={1} alignItems="center">
                        <Grid item xs={3}>
                           <FormInputSelectMultiple
                              name={`replacements.${index}.locales`}
                              label={`Replacement ${index + 1} Locales`}
                              options={locales ? locales.map(locale => ({
                                 id: locale,
                                 label: locale
                              })) : []}
                           />
                        </Grid>
                        <Grid item xs={3}>
                           <FormInputText
                              name={`replacements.${index}.search`}
                              label={`Search Text`}
                           />
                        </Grid>
                        <Grid item xs={3}>
                           <FormInputText
                              name={`replacements.${index}.replace`}
                              label={`Replace With`}
                           />
                        </Grid>
                        <Grid item xs={1}>
                           <Button
                              variant="outlined"
                              color="error"
                              onClick={() => removeReplacement(index)}
                              size="small"
                           >
                              Remove
                           </Button>
                        </Grid>
                     </Grid>
                  </Grid>
               ))}
               <Grid item xs={12}>
                  <Button
                     variant="outlined"
                     onClick={() => appendReplacement({locales: [], search: "", replace: ""})}
                  >
                     Add replacement rule
                  </Button>
               </Grid>
            </Grid>

         </PageContent>
      </FormProvider>
   </div>
}
